import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AUTH_CONFIG_IDS } from 'app/auth/auth.config';
import { SessionStore } from 'app/core/services/session.store';
import { ApprovedLegalDocumentsModalComponent } from 'app/legal-documents/components/approved-legal-documents-modal/approved-legal-documents-modal.component';
import { LegalDocumentsStore } from 'app/legal-documents/legal-documents.store';
import { LegalDocument } from 'app/legal-documents/model/LegalDocument';
import { LEGAL_DOCUMENTS_TYPES, LegalDocumentType } from 'app/legal-documents/model/LegalDocumentType';
import { IconComponent } from 'app/shared/components/icons/icon/icon.component';
import { MenuComponent } from 'app/shared/components/menu/menu.component';
import { PopoverComponent } from 'app/shared/components/popover/popover.component';
import { DEFAULT_LOCALE } from 'app/shared/i18n/i18n.config';
import { User } from 'app/shared/models/User';
import { AnalyticsService } from 'app/shared/services/analytics.service';
import { LocalStorageService } from 'app/shared/services/localStorage.service';
import { SecurityService } from 'app/shared/services/SecurityService';
import { getFaqPageUrl } from 'app/utils/url';

import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import {
    LOCALE_COUNTRY_STORAGE_KEY,
    resolveLanguageCode,
    resolveSelectedOption,
} from '../language-selector/locale-options';

@Component({
    selector: 'app-side-nav',
    imports: [
        TranslatePipe,
        IconComponent,
        PopoverComponent,
        MenuComponent,
        RouterLink,
        ApprovedLegalDocumentsModalComponent,
        LanguageSelectorComponent,
    ],
    templateUrl: './side-nav.component.html',
    styleUrl: './side-nav.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavComponent {
    private readonly analyticsService = inject(AnalyticsService);
    private readonly securityService = inject(SecurityService);
    private readonly translateService = inject(TranslateService);
    private readonly localStorageService = inject(LocalStorageService);
    private readonly legalDocuments = inject(LegalDocumentsStore);
    private readonly session = inject(SessionStore);

    // Only the mandatory documents reach the approval modal.
    private readonly analyticsDocTypes: Partial<Record<LegalDocumentType, 'data privacy' | 'CGU'>> = {
        [LEGAL_DOCUMENTS_TYPES.GENERAL_DATA_PROTECTION_REGULATION]: 'data privacy',
        [LEGAL_DOCUMENTS_TYPES.GENERAL_TERMS_AND_CONDITIONS_OF_USE]: 'CGU',
    };

    readonly userLogin = input<User | undefined>(undefined);

    readonly faqUrl = computed(() => getFaqPageUrl(this.userLogin()?.country ?? 'GB'));
    readonly legalNoticeUrl = this.legalDocuments.legalNoticeUrl;

    readonly legalDocumentApproval = this.legalDocuments.approvedDocuments;
    readonly legalDocumentApprovalModalOpen = signal(false);

    readonly languageSelectorOpen = signal(false);

    readonly localeTriggerLabel = this.resolveLocaleTriggerLabel();

    onSettingsClick(popover: PopoverComponent): void {
        popover.toggle();
        if (popover.isOpen()) {
            this.analyticsService.clickGlobalMenu();
        }
    }

    openLanguageSelector(popover: PopoverComponent): void {
        popover.close();
        this.languageSelectorOpen.set(true);
    }

    private resolveLocaleTriggerLabel(): string {
        const language = resolveLanguageCode(this.translateService.getCurrentLang() || DEFAULT_LOCALE);
        const country = this.localStorageService.getItem(LOCALE_COUNTRY_STORAGE_KEY);
        return resolveSelectedOption(language, country)?.country ?? '';
    }

    onConfigAlertClick(popover: PopoverComponent): void {
        this.analyticsService.clickAlertConfig();
        popover.close();
    }

    showSignedLegalDocModal(popover: PopoverComponent): void {
        this.legalDocumentApprovalModalOpen.set(true);
        popover.close();
    }

    logLegalDocumentAccessInAnalytics(document: LegalDocument): void {
        const docType = this.analyticsDocTypes[document.documentType];
        if (docType) {
            this.analyticsService.accessGlobalMenuDocument(docType);
        }
    }

    onLegalNoticeClick(popover: PopoverComponent): void {
        this.analyticsService.accessGlobalMenuDocument('legal notice');
        popover.close();
    }

    onFaqClick(popover: PopoverComponent): void {
        this.analyticsService.accessGlobalMenuDocument('faq');
        popover.close();
    }

    async logout(popover: PopoverComponent): Promise<void> {
        popover.close();
        await this.session.signOut();
        this.securityService.logout(AUTH_CONFIG_IDS.INSTALLER);
    }
}
